import type {ComponentType} from "react";
import { createBrowserRouter, Navigate, Outlet, redirect, RouteObject, useLocation } from "react-router";
import { QueryParamProvider } from "use-query-params";
import { ReactRouterAdapter } from "@utils/queryParamReactRouterAdapter";
import { extractReactRoutePaths, setReactRoutePaths } from "@utils/reactRoutePaths";
import { redirectIfUser, requireAuth } from "./pages/auth/authUtils";
import IndexPage from "./pages/IndexPage";
import ReleaseNotification from "@components/ReleaseNotification";
import { RouteSeo, SeoTitleProvider } from "./components/Seo";
import {RouteErrorBoundary} from "./components/RouteErrorBoundary";
import {useTranslation} from "react-i18next";

type PageModule = Record<string, unknown>;
type PageProps = Record<string, unknown>;

const RouterFallback = () => {
    const {t} = useTranslation();

    return <div className="p-4 text-center">{t('common.loading')}</div>;
};

const pageModules = import.meta.glob([
    "./pages/**/*.tsx",
    "!./pages/IndexPage.tsx",
    "!./pages/GameHomePage.tsx"
]) as Record<
    string,
    () => Promise<PageModule>
>;

const lazyPage = (
    modulePath: string,
    exportName = "default",
    props: PageProps = {}
): RouteObject["lazy"] => async () => {
    const loadModule = pageModules[modulePath];

    if (!loadModule) {
        throw new Error(`Missing lazy route module: ${modulePath}`);
    }

    const module = await loadModule();
    const Component = module[exportName];

    if (typeof Component !== "function") {
        throw new Error(
            `Missing lazy route export: ${modulePath}#${exportName}`
        );
    }

    const Page = Component as ComponentType<PageProps>;

    return {
        Component: () => <Page {...props} />
    };
};

const CollectionRoute = () => {
    const location = useLocation();
    const pathname = location.pathname
        .replace(/^\/problems\/collection-progress(?=\/|$)/, "/collections/progress")
        .replace(/^\/problems\/collections(?=\/|$)/, "/collections")
        .replace(/\/+$/, "");

    if (pathname !== location.pathname) {
        return (
            <Navigate
                replace
                to={{pathname, search: location.search, hash: location.hash}}
                state={location.state}
            />
        );
    }

    return <Outlet />;
};

const LegacyUserRoute = () => {
    const location = useLocation();
    const pathname = location.pathname
        .replace(/^\/user(?=\/|$)/, "/users")
        .replace(/^\/problems\/by-author\/([^/]+)\/?$/, "/users/$1/problems")
        .replace(/^(\/users\/[^/]+)\/problem-list\/?$/, "$1/problems")
        .replace(/^(\/users\/[^/]+)\/favorite-list\/?$/, "$1/favorites");

    return (
        <Navigate
            replace
            to={{
                pathname,
                search: location.search,
                hash: location.hash,
            }}
            state={location.state}
        />
    );
};

const routeConfig: RouteObject[] = [
    {
        path: "/",
        element: (
            <QueryParamProvider adapter={ReactRouterAdapter}>
                <SeoTitleProvider>
                    <RouteSeo />
                    <Outlet />
                    <ReleaseNotification />
                </SeoTitleProvider>
            </QueryParamProvider>
        ),
        errorElement: <RouteErrorBoundary />,
        children: [
            {
                path: "/",
                element: <IndexPage />,
            },
            {
                path: "/get-pro",
                lazy: lazyPage("./pages/GetPro.tsx"),
                loader: requireAuth
            },
            {
                path: "/potd/streaks",
                lazy: lazyPage("./pages/DailyStreaks.tsx")
            },
            {
                path: "/user/*",
                element: <LegacyUserRoute />
            },
            {
                path: "/users/:username",
                lazy: lazyPage("./pages/PublicProfile.tsx")
            },
            {
                path: "/users/:username/problems",
                lazy: lazyPage("./pages/PublicUserProblems.tsx", "PublicUserProblems", {variant: "created"})
            },
            {
                path: "/problems/by-author/:username",
                element: <LegacyUserRoute />
            },
            {
                path: "/users/:username/favorites",
                lazy: lazyPage("./pages/PublicUserProblems.tsx", "PublicUserProblems", {variant: "favorites"})
            },
            {
                path: "/users/:username/problem-list",
                element: <LegacyUserRoute />
            },
            {
                path: "/users/:username/favorite-list",
                element: <LegacyUserRoute />
            },
            {
                path: "/user-search",
                lazy: lazyPage("./pages/UserSearch.tsx", "UserSearch")
            },
            {
                path: "/rush",
                lazy: lazyPage("./pages/rushes/RushHome.tsx", "RushHome")
            },
            {
                path: "/rushes/:id",
                lazy: lazyPage("./pages/rushes/Rush.tsx", "Rush")
            },
            {
                path: "/rushes/:id/result",
                lazy: lazyPage("./pages/rushes/RushResult.tsx", "RushResult")
            },
            {
                path: "/rushes/leaderboard",
                lazy: lazyPage("./pages/rushes/RushLeaderboard.tsx", "RushLeaderboard")
            },
            {
                path: "/rushes/live",
                lazy: lazyPage("./pages/rushes/LiveRushes.tsx", "LiveRushes")
            },
            {
                path: "/collection-tests/:id",
                lazy: lazyPage("./pages/collectionTests/CollectionTest.tsx", "CollectionTest")
            },
            {
                path: "/collection-tests/:id/result",
                lazy: lazyPage("./pages/collectionTests/CollectionTestResult.tsx", "CollectionTestResult")
            },
            {
                path: "/collection-tests/leaderboard/:collectionId",
                lazy: lazyPage("./pages/collectionTests/CollectionTestLeaderboard.tsx", "CollectionTestLeaderboard")
            },
            {
                path: "/problems/:id/research",
                lazy: lazyPage("./pages/Analysis.tsx", "Analysis")
            },
            {
                path: "/problems/:id/paths-history",
                lazy: lazyPage("./pages/PathHistory.tsx", "PathHistory"),
                loader: requireAuth
            },
            {
                path: "/analysis",
                lazy: lazyPage("./pages/Analysis.tsx", "Analysis")
            },
            {
                path: "/collections",
                element: <CollectionRoute />,
                children: [
                    {
                        index: true,
                        lazy: lazyPage("./pages/Collections.tsx", "Collections")
                    },
                    {
                        path: "progress",
                        lazy: lazyPage("./pages/CollectionProgress.tsx")
                    },
                    {
                        path: ":collectionId",
                        lazy: lazyPage("./pages/Collection.tsx", "Collection")
                    },
                    {
                        path: "create",
                        lazy: lazyPage("./pages/CreateCollection.tsx", "CreateCollection"),
                        loader: requireAuth
                    },
                    {
                        path: ":collectionId/edit",
                        lazy: lazyPage("./pages/EditCollection.tsx", "EditCollection"),
                        loader: requireAuth
                    }
                ]
            },
            {
                path: "/problems/collections/*",
                element: <CollectionRoute />
            },
            {
                path: "/problems/collection-progress",
                element: <CollectionRoute />
            },
            {
                path: "/invasions",
                lazy: lazyPage("./pages/Invasions.tsx", "Invasions")
            },
            {
                path: "/invasions/create",
                lazy: lazyPage("./pages/CreateInvasion.tsx", "CreateInvasion")
            },
            {
                path: "/invasions/:id/edit",
                lazy: lazyPage("./pages/CreateInvasion.tsx", "CreateInvasion")
            },
            {
                path: "/invasions/:id",
                lazy: lazyPage("./pages/InvasionDetail.tsx", "InvasionDetail")
            },
            {
                path: "/invasions/:id/attempts/:attemptId",
                lazy: lazyPage("./pages/InvasionAttempt.tsx", "InvasionAttempt")
            },
            {
                path: "/invasions/:id/research",
                lazy: lazyPage("./pages/InvasionResearch.tsx", "InvasionResearch")
            },
            {
                path: "/scenario-cases",
                lazy: lazyPage("./pages/ScenarioCasesDashboard.tsx")
            },
            {
                path: "/scenario-cases/:id",
                lazy: lazyPage("./pages/ScenarioCaseDetail.tsx")
            },
            {
                path: "/scenarios/:id/cases",
                lazy: lazyPage("./pages/ScenarioCasesList.tsx")
            },
            {
                path: "/settings/avatar",
                lazy: lazyPage("./pages/SettingsAvatar.tsx", "SettingsAvatar"),
                loader: requireAuth
            },
            {
                path: "/settings/avatar/library",
                lazy: lazyPage("./pages/SettingsAvatarLibrary.tsx", "SettingsAvatarLibrary"),
                loader: requireAuth
            },
            {
                path: "/suggestions",
                lazy: lazyPage("./pages/Suggestions.tsx", "Suggestions")
            },
            {
                path: "/suggestions/new",
                lazy: lazyPage("./pages/SuggestionsEdit.tsx", "SuggestionsEdit", {variant: "new"})
            },
            {
                path: "/suggestions/:id",
                lazy: lazyPage("./pages/SuggestionsEdit.tsx", "SuggestionsEdit")
            },
            {
                path: "/suggestions/:id/edit",
                lazy: lazyPage("./pages/SuggestionsEdit.tsx", "SuggestionsEdit", {variant: "edit"})
            },
            {
                path: "/suggestions/:id/resolve",
                lazy: lazyPage("./pages/SuggestionsEdit.tsx", "SuggestionsEdit", {variant: "resolve"}),
                loader: requireAuth
            },
            {
                path: "/dashboard/suggestions",
                lazy: lazyPage("./pages/dashboard/MySuggestions.tsx", "MySuggestions"),
                loader: requireAuth
            },
            {
                path: "/dashboard/problems",
                lazy: lazyPage("./pages/dashboard/MyProblems.tsx", "MyProblems"),
                loader: requireAuth
            },
            {
                path: "/dashboard/favorites",
                lazy: lazyPage("./pages/dashboard/MyFavorites.tsx", "MyFavorites"),
                loader: requireAuth
            },
            {
                path: "/dashboard/notifications",
                lazy: lazyPage("./pages/dashboard/MyNotifications.tsx", "MyNotifications"),
                loader: requireAuth
            },
            {
                path: "/dashboard/comments",
                lazy: lazyPage("./pages/dashboard/MyComments.tsx", "MyComments"),
                loader: requireAuth
            },
            {
                path: "/dashboard/flagged",
                lazy: lazyPage("./pages/dashboard/MyFlaggedProblems.tsx", "MyFlaggedProblems"),
                loader: requireAuth
            },
            {
                path: "/dashboard/edits",
                lazy: lazyPage("./pages/dashboard/MyEdits.tsx", "MyEdits"),
                loader: requireAuth
            },
            {
                path: "/dashboard/comments-on-my-problems",
                lazy: lazyPage("./pages/dashboard/MyProblemComments.tsx", "MyProblemComments"),
                loader: requireAuth
            },
            {
                path: "/dashboard/deactivated-problems",
                lazy: lazyPage("./pages/dashboard/MyDeactivatedProblems.tsx", "MyDeactivatedProblems"),
                loader: requireAuth
            },
            {
                path: "/dashboard/sandbox",
                lazy: lazyPage("./pages/dashboard/MySandboxProblems.tsx", "MySandboxProblems"),
                loader: requireAuth
            },
            {
                path: "/dashboard/suggestions-on-my-problems",
                lazy: lazyPage("./pages/dashboard/MySuggestions.tsx", "MySuggestions"),
                loader: requireAuth
            },
            {
                path: "/dashboard/suggestions-on-my-collections",
                lazy: lazyPage("./pages/dashboard/MySuggestionsOnMyCollections.tsx", "MySuggestionsOnMyCollections"),
                loader: requireAuth
            },
            {
                path: "/dashboard/friendship-requests",
                lazy: lazyPage("./pages/dashboard/FriendshipRequests.tsx", "FriendshipRequests"),
                loader: requireAuth
            },
            {
                path: "/dashboard/collections",
                lazy: lazyPage("./pages/dashboard/MyCollections.tsx"),
                loader: requireAuth
            },
            {
                path: "/dashboard/attempts",
                lazy: lazyPage("./pages/dashboard/MyAttemptsPage.tsx"),
                loader: requireAuth
            },
            {
                path: "/dashboard/collection-tests",
                lazy: lazyPage("./pages/collectionTests/CollectionTests.tsx", "CollectionTests"),
                loader: requireAuth
            },
            {
                path: "/dashboard",
                lazy: lazyPage("./pages/DashboardOverview.tsx", "DashboardOverview"),
                loader: requireAuth
            },
            {
                path: "/dashboard/me",
                loader: () => redirect("/dashboard")
            },
            {
                path: "/users/:username/attempts",
                lazy: lazyPage("./pages/UserAttemptsPage.tsx")
            },
            {
                path: "/leaderboard",
                lazy: lazyPage("./pages/Leaderboard.tsx")
            },
            {
                path: "/time-trials",
                lazy: lazyPage("./pages/timeTrials/TimeTrialsPage.tsx", "TimeTrialsPage")
            },
            {
                path: "/time-trials-new",
                lazy: lazyPage("./pages/timeTrials/TimeTrialsNew.tsx", "TimeTrialsNew")
            },
            {
                path: "/time-trials/:id",
                lazy: lazyPage("./pages/timeTrials/TimeTrial.tsx", "TimeTrial")
            },
            {
                path: "/time-trials/leaderboard",
                lazy: lazyPage("./pages/timeTrials/TimeTrialLeaderboard.tsx")
            },
            {
                path: "/time-trials/:id/result",
                lazy: lazyPage("./pages/timeTrials/TimeTrialResult.tsx")
            },
            {
                path: "/daily-leaderboard",
                lazy: lazyPage("./pages/DailyLeaderboard.tsx")
            },
            {
                path: "/tournaments/:id/lobby",
                lazy: lazyPage("./pages/tournaments/TournamentLobby.tsx", "TournamentLobby")
            },
            {
                path: "/tournaments/:id/play",
                lazy: lazyPage("./pages/tournaments/TournamentPlay.tsx", "TournamentPlay")
            },
            {
                path: "/tournaments/:id/results",
                lazy: lazyPage("./pages/tournaments/TournamentResults.tsx", "TournamentResults")
            },
            {
                path: "/tournaments/:id/rules",
                lazy: lazyPage("./pages/tournaments/TournamentRules.tsx", "TournamentRules")
            },
            {
                path: "/tournaments/:tournamentId/players/:playerId/results",
                lazy: lazyPage("./pages/tournaments/TournamentPlayerResults.tsx", "TournamentPlayerResults")
            },
            {
                path: "/tournaments",
                lazy: lazyPage("./pages/tournaments/Tournaments.tsx", "Tournaments")
            },
            {
                path: "/settings/token",
                lazy: lazyPage("./pages/SettingsToken.tsx", "SettingsToken"),
                loader: requireAuth
            },
            {
                path: "/settings/profile",
                lazy: lazyPage("./pages/SettingsProfile.tsx", "SettingsProfile"),
                loader: requireAuth
            },
            {
                path: "/settings/notifications",
                lazy: lazyPage("./pages/SettingsNotifications.tsx", "SettingsNotifications"),
                loader: requireAuth
            },
            {
                path: "/notifications",
                lazy: lazyPage("./pages/NotificationsHistory.tsx", "NotificationsHistory"),
                loader: requireAuth
            },
            {
                path: "/settings/privacy",
                lazy: lazyPage("./pages/SettingsPrivacy.tsx", "SettingsPrivacy"),
                loader: requireAuth
            },
            {
                path: "/settings/password",
                lazy: lazyPage("./pages/SettingsPassword.tsx", "SettingsPassword"),
                loader: requireAuth
            },
            {
                path: "/settings/delete-account",
                lazy: lazyPage("./pages/SettingsDeleteAccount.tsx", "SettingsDeleteAccount"),
                loader: requireAuth
            },
            {
                path: "/settings/beta",
                lazy: lazyPage("./pages/SettingsBeta.tsx", "SettingsBeta"),
                loader: requireAuth
            },
            {
                path: "/settings/theme",
                lazy: lazyPage("./pages/SettingsTheme.tsx", "SettingsTheme"),
                loader: requireAuth
            },
            {
                path: "/settings/repetition-training",
                lazy: lazyPage("./pages/SettingsRepetitionTraining.tsx", "SettingsRepetitionTraining"),
                loader: requireAuth
            },
            {
                path: "/settings/board-theme",
                lazy: lazyPage("./pages/SettingsBoardTheme.tsx", "SettingsBoardTheme"),
                loader: requireAuth
            },
            {
                path: "/leaderboard",
                lazy: lazyPage("./pages/Leaderboard.tsx")
            },
            {
                path: "/users/:username/comments",
                lazy: lazyPage("./pages/UserComments.tsx", "UserComments")
            },
            {
                path: "/problems/:id",
                lazy: lazyPage("./pages/Problem.tsx")
            },
            {
                path: "/problems/:id/rating-history",
                lazy: lazyPage("./pages/ProblemRatingHistory.tsx", "ProblemRatingHistory")
            },
            {
                path: "/problems/:id/potd-attempts",
                lazy: lazyPage("./pages/POTDAttemptsPage.tsx")
            },
            {
                path: "/problems/:id/potd-attempts/:difficulty",
                lazy: lazyPage("./pages/POTDAttemptsPage.tsx", "LegacyPOTDAttemptsPage")
            },
            {
                path: "/problems/:id/my-attempts",
                lazy: lazyPage("./pages/MyAttemptsOnProblem.tsx", "MyAttemptsOnProblem")
            },
            {
                path: "/users/:name/achievements",
                lazy: lazyPage("./pages/UserAchievement.tsx")
            },
            {
                path: "/achievements/:code",
                lazy: lazyPage("./pages/AchievementInfo.tsx", "AchievementInfo")
            },
            {
                path: "/tools/sgf/difftool",
                lazy: lazyPage("./pages/SgfDiffTool.tsx", "SgfDiffTool")
            },
            {
                path: "/problems/filter",
                lazy: lazyPage("./pages/Problems.tsx", "Problems")
            },
            {
                path: "/problems",
                lazy: lazyPage("./pages/Problems.tsx", "Problems")
            },
            {
                path: "/problems/next",
                lazy: lazyPage("./pages/NextProblemRedirect.tsx", "NextProblemRedirect")
            },
            {
                path: "/statistics",
                lazy: lazyPage("./pages/Statistics.tsx", "Statistics")
            },
            {
                path: "/download",
                lazy: lazyPage("./pages/Download.tsx", "Download")
            },
            {
                path: "/rss",
                lazy: lazyPage("./pages/Rss.tsx", "Rss")
            },
            {
                path: "/release-notes",
                lazy: lazyPage("./pages/ReleaseNotes.tsx", "ReleaseNotes")
            },
            {
                path: "/sitemap",
                lazy: lazyPage("./pages/SiteMap.tsx", "SiteMap")
            },
            {
                path: "/site-map",
                lazy: lazyPage("./pages/SiteMap.tsx", "SiteMap")
            },
            {
                path: "/under-construction",
                lazy: lazyPage("./pages/UnderConstruction.tsx", "UnderConstruction")
            },
            {
                path: "/article/:slug",
                lazy: lazyPage("./pages/Article.tsx", "Article")
            },
            {
                path: "/problems/recommendations",
                lazy: lazyPage("./pages/Recommendations.tsx", "Recommendations")
            },
            {
                path: "/problems/practice",
                lazy: lazyPage("./pages/Practice.tsx", "Practice"),
                loader: requireAuth
            },
            {
                path: "/srs",
                lazy: lazyPage("./pages/SrsHome.tsx", "SrsHome"),
                loader: requireAuth
            },
            {
                path: "/problems/search/text",
                lazy: lazyPage("./pages/ProblemSearchText.tsx", "LegacyProblemTextSearch")
            },
            {
                path: "/problems/search",
                lazy: lazyPage("./pages/ProblemSearchText.tsx", "ProblemSearchText")
            },
            {
                path: "/problems/flagged",
                lazy: lazyPage("./pages/FlaggedProblems.tsx", "FlaggedProblems")
            },
            {
                path: "/comments/recent",
                lazy: lazyPage("./pages/RecentComments.tsx", "RecentComments")
            },
            {
                path: "/problems/create",
                lazy: lazyPage("./pages/ProblemDraft.tsx", "ProblemDraft", {variant: "new"}),
                loader: requireAuth
            },
            {
                path: "/problems/:id/edit",
                lazy: lazyPage("./pages/ProblemDraft.tsx", "ProblemDraft", {variant: "edit"}),
                loader: requireAuth
            },
            {
                path: "/problems/search/pattern",
                lazy: lazyPage("./pages/SearchPattern.tsx", "SearchPattern")
            },
            {
                path: "/problems/need-rating",
                lazy: lazyPage("./pages/NeedRating.tsx"),
                loader: requireAuth
            },
            {
                path: "/problems/lint-issues",
                lazy: lazyPage("./pages/LintIssues.tsx", "LintIssues")
            },
            {
                path: "/tournaments/create",
                lazy: lazyPage("./pages/tournaments/CreateTournament.tsx", "CreateTournament"),
                loader: requireAuth
            },
            {
                path: "/tournaments/:id/edit",
                lazy: lazyPage("./pages/tournaments/CreateTournament.tsx", "CreateTournament"),
                loader: requireAuth
            },
            {
                path: "/dashboard/tournaments",
                lazy: lazyPage("./pages/tournaments/MyTournaments.tsx", "MyTournaments"),
                loader: requireAuth
            },
            {
                path: "/problems/:id/edit-history",
                lazy: lazyPage("./pages/EditHistory.tsx", "EditHistory")
            },
            {
                path: "/friends",
                lazy: lazyPage("./pages/Friends.tsx", "Friends"),
                loader: requireAuth
            },
            {
                path: "/dashboard/friends",
                lazy: lazyPage("./pages/Friends.tsx", "Friends"),
                loader: requireAuth
            },
            {
                path: "/friendship-requests",
                lazy: lazyPage("./pages/dashboard/FriendshipRequests.tsx", "FriendshipRequests"),
                loader: requireAuth
            },
            {
                path: "/problems/:id/flaghist",
                lazy: lazyPage("./pages/Flaghist.tsx", "Flaghist")
            },
            {
                path: "/problems/:id/cleared-flags",
                lazy: lazyPage("./pages/ClearedFlaghist.tsx", "ClearedFlaghist")
            },
            {
                path: "/groups",
                lazy: lazyPage("./pages/groups/Groups.tsx", "Groups")
            },
            {
                path: "/groups/:id/edit",
                lazy: lazyPage("./pages/groups/EditGroup.tsx", "EditGroup"),
                loader: requireAuth
            },
            {
                path: "/groups/create",
                lazy: lazyPage("./pages/groups/EditGroup.tsx", "EditGroup"),
                loader: requireAuth
            },
            {
                path: "/dashboard/groups",
                lazy: lazyPage("./pages/groups/MyGroups.tsx", "MyGroups"),
                loader: requireAuth
            },
            {
                path: "/groups/:id",
                lazy: lazyPage("./pages/groups/Group.tsx", "Group")
            },
            {
                path: "/levels",
                lazy: lazyPage("./pages/Levels.tsx"),
                loader: requireAuth
            },
            {
                path: "/problems/curate",
                lazy: lazyPage("./pages/ProblemCurationLandingPage.tsx", "ProblemCurationLandingPage")
            },
            {
                path: "/curations/:id",
                lazy: lazyPage("./pages/ProblemCuration.tsx", "ProblemCuration"),
                loader: requireAuth
            },
            {
                path: "/problem-drafts/:id/resolve",
                lazy: lazyPage("./pages/ProblemDraftResolve.tsx", "ProblemDraftResolve"),
                loader: requireAuth
            },
            {
                path: "/training/cooldown",
                lazy: lazyPage("./pages/TrainingCooldown.tsx")
            },
            {
                lazy: lazyPage("./pages/AuthPageLayout.tsx"),
                path: "/auth",
                loader: redirectIfUser,
                children: [
                    {
                        path: "login",
                        lazy: lazyPage("./pages/auth/LoginPage.tsx", "LoginPage")
                    },
                    {
                        path: "register",
                        lazy: lazyPage("./pages/auth/RegisterPage.tsx", "RegisterPage")
                    },
                    {
                        path: "forgot-password",
                        lazy: lazyPage("./pages/auth/ForgotPasswordPage.tsx", "ForgotPasswordPage")
                    },
                    {
                        path: "change-password/:code",
                        lazy: lazyPage("./pages/auth/ChangePasswordPage.tsx", "ChangePasswordPage")
                    }
                ]
            },
            {
                path: "*",
                handle: {noindex: true},
                lazy: lazyPage("./pages/NotFoundHandler.tsx")
            }
        ],
        hydrateFallbackElement: <RouterFallback />
    }
];

setReactRoutePaths(extractReactRoutePaths(routeConfig));

export const router: ReturnType<typeof createBrowserRouter> =
    createBrowserRouter(routeConfig, {
        future: {}
    });
